import { useCallback, useEffect, useMemo, useState } from 'react';

import { BulkImportAssetsRequestDTO, getDateFnsFormatStringWithUserTimezone } from '@gearbelt/global';
import { Button, buttonVariants } from '@gearbelt/ui/components';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { GearbeltAssetType } from '@site-mate/sitemate-global-shared/lib';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { parse } from 'date-fns';
import { Link } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { z } from 'zod';

import DeleteIcon from '@/components/icons/delete';
import QuestionIcon from '@/components/icons/question';
import UploadIcon from '@/components/icons/upload';
import { TablePageHeaderActionButton } from '@/components/table-page/table-page-header';
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Dropzone, DropzoneContent, DropzoneDescription, DropzoneInput, DropzoneTitle } from '@/components/ui/dropzone';
import { useToast } from '@/components/ui/toaster';
import { cn } from '@/lib/utils';
import { assetCategoryKeys, getAssetCategoriesQueryOptions } from '@/queries/asset-categories';
import { assetKeys } from '@/queries/assets';
import { ApiService } from '@/services/api-v2';
import { useUserStore } from '@/stores/user.store';

import { assetSheetImportSchema } from './asset-form';

export default function BulkAssetImportDialog({
  open: openProp,
  onOpenChange,
  noTrigger = false,
}: {
  open?: boolean;
  onOpenChange?: (open: boolean, success?: boolean) => void;
  noTrigger?: boolean;
}) {
  const userTimezone = useUserStore.getState().user?.timezone;
  const toast = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(openProp ?? false);
  const [file, setFile] = useState<File | null>(null);
  const [data, setData] = useState<z.infer<typeof assetSheetImportSchema>[]>([]);

  const { data: assetCategories } = useQuery(getAssetCategoriesQueryOptions());

  const { mutateAsync: bulkImportAssets, isPending: bulkImportAssetsPending } = useMutation({
    mutationFn: (values: z.infer<typeof assetSheetImportSchema>[]) => {
      return ApiService.BulkImportAssets({
        assets: values.map<BulkImportAssetsRequestDTO['assets'][number]>((asset) => ({
          ...asset,
          tags: asset.tags || [],
        })),
      });
    },
    onSuccess: async () => {
      await queryClient.resetQueries({
        queryKey: assetKeys.all,
      });
      handleOpenChange(false, true);
      toast.success('Assets imported successfully');
    },
    onError: (error) => {
      toast.error(`Error adding asset: ${error.message}`);
    },
  });

  const { mutateAsync: createAssetCategory } = useMutation({
    mutationFn: async (values: { name: string; type: string }) => {
      return await (
        await ApiService.CreateWorkspaceAssetCategory({
          name: values.name,
          assetType: values.type as GearbeltAssetType,
        })
      ).json();
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({
        queryKey: assetCategoryKeys.all,
      });
    },
    onError: (error) => {
      toast.error(`Error adding asset category: ${error.message}`);
    },
  });

  useEffect(() => {
    setOpen(openProp ?? false);
  }, [openProp]);

  const handleOpenChange = (open: boolean, success?: boolean) => {
    onOpenChange?.(open, success);
    setOpen(open);

    if (!open) {
      setFile(null);
      setData([]);
    }
  };

  const dateFormatString = getDateFnsFormatStringWithUserTimezone(userTimezone);
  const parseDateFormat = useCallback((stringDate: string) => parse(stringDate, dateFormatString, new Date()), [dateFormatString]);

  const assetSheetSchema = useMemo(
    () =>
      z.object({
        'Asset ID': z.coerce.string({ message: 'Asset ID is required.' }).transform((val) => val.trim()),
        'Asset Type': z
          .string({ message: 'Asset Type is required.' })
          .transform((val) => val.trim())
          .refine((value) => Object.values(GearbeltAssetType).includes(value as GearbeltAssetType), {
            message: 'Asset Type is invalid.',
          }),
        'Asset Category': z
          .string()
          .optional()
          .transform((val) => val?.trim()),
        Manufacturer: z.coerce
          .string()
          .optional()
          .transform((val) => val?.trim()),
        Model: z.coerce
          .string()
          .optional()
          .transform((val) => val?.trim()),
        Class: z.coerce
          .string()
          .optional()
          .transform((val) => val?.trim()),
        Year: z.coerce.number().int('Hours must be a whole number').optional(),
        Registration: z.coerce
          .string()
          .optional()
          .transform((val) => val?.trim()),
        'Serial Number': z.coerce
          .string()
          .optional()
          .transform((val) => val?.trim()),
        'Engine Number': z.coerce
          .string()
          .optional()
          .transform((val) => val?.trim()),
        'Chassis/VIN': z.coerce
          .string()
          .optional()
          .transform((val) => val?.trim()),
        Comments: z.coerce
          .string()
          .optional()
          .transform((val) => val?.trim()),
        Tags: z
          .string()
          .optional()
          .transform((val) =>
            val
              ? val
                  .trim()
                  .split(',')
                  .map((tag) => tag.trim())
              : [],
          ),
      }),
    [dateFormatString, parseDateFormat],
  );

  const handleImport = (file: File) => {
    if (!assetCategories) {
      toast.error('Data has not loaded. Please try again later.');
      return;
    }

    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          if (event.target && event.target.result) {
            const data = new Uint8Array(event.target.result as ArrayBuffer);
            const workbook = XLSX.read(data, { type: 'array', raw: true });
            const sheetName = workbook.SheetNames[0];
            const sheet = workbook.Sheets[sheetName];

            const expectedColumns = Object.keys(assetSheetSchema.shape);
            const sheetColumns = Object.keys(XLSX.utils.sheet_to_json(sheet, { defval: null })[0] || {});
            const missingColumns = expectedColumns.filter((col) => !sheetColumns.includes(col));

            if (missingColumns.length > 0) {
              toast.error(`File has missing columns ${missingColumns.join(', ')}. Please use the Excel template provided.`);
              return;
            }

            const jsonData = XLSX.utils.sheet_to_json(sheet, { raw: true, rawNumbers: false });

            const parsedItems: z.infer<typeof assetSheetSchema>[] = [];
            const uniqueAssetIds = new Map<string, number>();

            for (const [idx, item] of jsonData.entries()) {
              // Parse and validate schema
              const parsedItem = assetSheetSchema.safeParse(item);
              if (!parsedItem.success) {
                const errorIssue = parsedItem.error.issues[0];
                const errorMessage =
                  errorIssue.code === 'invalid_type' || errorIssue.code === 'custom'
                    ? `Row #${idx + 1 + 1}: ${errorIssue.message}.`
                    : `Row #${idx + 1 + 1} is missing the required field "${errorIssue.path[1].toString()}".`;
                toast.error(errorMessage);
                return;
              }

              // Check for unique Asset ID
              const assetId = (item as Record<string, unknown>)['Asset ID'] as string;
              const firstAppearanceIndex = uniqueAssetIds.get(assetId);
              if (firstAppearanceIndex !== undefined) {
                toast.error(`Asset ID ${assetId} must be unique, first appeared on Row #${firstAppearanceIndex + 1 + 1}`);
                return;
              }
              uniqueAssetIds.set(assetId, idx);

              parsedItems.push(parsedItem.data);
            }

            const assetsToImport = parsedItems.map((asset) => ({
              internalId: asset['Asset ID'],
              type: asset['Asset Type'],
              categoryId: assetCategories.find((category) => category.name === asset['Asset Category'])?.id || asset['Asset Category'],
              manufacturer: asset.Manufacturer,
              class: asset.Class,
              model: asset.Model,
              year: asset.Year,
              registration: asset.Registration,
              serialNumber: asset['Serial Number'],
              engineNumber: asset['Engine Number'],
              chassisVin: asset['Chassis/VIN'],
              comments: asset.Comments,
              tags: asset.Tags,
            }));

            const validationSchema = assetSheetImportSchema;
            const validationResult = z.array(validationSchema).safeParse(assetsToImport);
            if (!validationResult.success) {
              console.info(validationResult);
              toast.error(
                `Row #${Number(validationResult.error.issues[0].path[0]) + 1} contains invalid values or formatting. Please use the Excel template provided.`,
              );
              return;
            }

            // validationResult.data is compatible with the state type since assetSheetImportSchema
            // is a subset of assetSheetImportSchemaWithLegacyFields (legacy fields are optional)
            setData(validationResult.data);
            setFile(file);
          }
        } catch (error) {
          console.error('Failed to parse the import file:', error);
          toast.error('Could not read the file. Please use the Excel template provided.');
        }
      };
      reader.readAsArrayBuffer(file);
    }
  };

  async function handleSubmit() {
    const categoryGroups = new Map<string, { assets: typeof data; type: string }>();

    data.forEach((asset) => {
      if (asset.categoryId && !z.string().uuid().safeParse(asset.categoryId).success) {
        const existing = categoryGroups.get(asset.categoryId);
        if (existing) {
          existing.assets.push(asset);
        } else {
          categoryGroups.set(asset.categoryId, { assets: [asset], type: asset.type });
        }
      }
    });

    const categoryMappings = new Map<string, string>();
    const categoryCreationPromises = Array.from(categoryGroups.entries()).map(async ([categoryName, { type }]) => {
      const newCategory = await createAssetCategory({
        name: categoryName,
        type,
      });
      categoryMappings.set(categoryName, newCategory.id);
    });

    await Promise.all(categoryCreationPromises);

    const transformedData = data.map((asset) => {
      if (asset.categoryId && !z.string().uuid().safeParse(asset.categoryId).success) {
        const newCategoryId = categoryMappings.get(asset.categoryId);
        if (!newCategoryId) {
          throw new Error(`Failed to create category mapping for "${asset.categoryId}". This should never happen.`);
        }
        return {
          ...asset,
          categoryId: newCategoryId,
        };
      }
      return asset;
    });

    await bulkImportAssets(transformedData);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {!noTrigger && (
        <DialogTrigger asChild>
          <TablePageHeaderActionButton icon={<UploadIcon />} label="Upload Assets" />
        </DialogTrigger>
      )}
      <DialogContent className="flex flex-col gap-4">
        {/* Visually hidden for a11y */}
        <VisuallyHidden>
          <DialogDescription>
            To bulk import your assets, please use the Excel template provided below. <b>Asset ID</b> and <b>Asset Type</b> are required fields, everything else
            is optional.
          </DialogDescription>
        </VisuallyHidden>

        <DialogTitle>Upload Assets</DialogTitle>

        <div>
          <div className="text-sm">
            To bulk import your assets, please use the Excel template provided below. <b>Asset ID</b> and <b>Asset Type</b> are required fields, everything else
            is optional.
            <br />
            <br />
            It is important that the columns match this format exactly. Cells that are empty or have incorrect formatting will be ignored.
            <br />
            <br />
            <a href="/gearbelt-asset-import-template-new.xlsx" download className="text-default-blue underline">
              Download Excel template
            </a>
          </div>

          <div className="flex flex-col gap-2">
            <Link
              to="https://help.sitemate.com/en/articles/10094910-how-to-bulk-import-assets-to-gearbelt"
              target="_blank"
              className={cn(buttonVariants({ variant: 'grey', size: 'sm' }), 'ml-auto flex items-center gap-1')}>
              <QuestionIcon className="h-4 w-4" />
              Learn how to bulk import
            </Link>

            <Dropzone
              accept={{
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
                'application/vnd.ms-excel': ['.xls'],
                'text/csv': ['.csv'],
              }}
              maxSize={10 * 1024 * 1024}
              maxFiles={1}
              onDropAccepted={(files) => handleImport(files[0])}
              onDropRejected={(fileRejections) => {
                const rejection = fileRejections[0];
                if (rejection.errors.some((e) => e.code === 'file-invalid-type')) {
                  toast.error('File type is not supported.');
                } else if (rejection.errors.some((e) => e.code === 'file-too-large')) {
                  toast.error('File exceeds 10MB.');
                } else {
                  toast.error('File is invalid. Please check the file and try again.');
                }
              }}>
              <DropzoneContent
                className={cn(
                  'border-grey-4 flex w-full items-center justify-center border-2',
                  file ? 'cursor-default border-solid' : 'bg-grey-1 border-dashed',
                )}>
                {file === null ? (
                  <>
                    <DropzoneTitle>
                      <UploadIcon className="text-secondary-text size-8" />
                    </DropzoneTitle>
                    <DropzoneDescription>Drag and drop your files here or click to upload</DropzoneDescription>
                    <DropzoneInput />
                  </>
                ) : (
                  <div className="flex w-full items-center justify-between gap-2 text-sm">
                    <div>{file.name}</div>
                    <DeleteIcon className="text-default-red size-4 cursor-pointer" onClick={() => setFile(null)} />
                  </div>
                )}
              </DropzoneContent>
            </Dropzone>

            <div className="text-secondary-text text-sm">
              <div>File types: .xlsx, .xls, .csv</div>
              <div>File size limit: 10MB</div>
            </div>
          </div>
        </div>

        <Button variant="success" className="w-fit" disabled={bulkImportAssetsPending || data.length === 0} onClick={handleSubmit}>
          Import
        </Button>
      </DialogContent>
    </Dialog>
  );
}
